import React from 'react';

import { cn } from '@/utils/classname';

export interface ClickableCountProps {
  /**
   * The count/number to display
   */
  count: number;
  /**
   * Click handler - only called if count > 0
   */
  onClick?: () => void;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Size variant for the count
   * @default 'md'
   */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Whether to show the count even if it's 0
   * @default true
   */
  showZero?: boolean;
}

const sizeClasses = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-md',
  lg: 'text-lg',
  xl: 'text-xl',
};

/**
 * ClickableCount - A reusable component for displaying clickable count values
 *
 * Automatically adds cursor-pointer and hover effects when the count is > 0 and onClick is provided.
 * Perfect for statistics, metrics, and monitoring dashboards.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ClickableCount
 *   count={42}
 *   onClick={() => navigate('/vulnerabilities')}
 * />
 *
 * // With custom styling
 * <ClickableCount
 *   count={total}
 *   onClick={() => handleClick()}
 *   size="lg"
 *   className="font-bold"
 * />
 *
 * // Non-clickable (no onClick or count is 0)
 * <ClickableCount count={0} />
 * ```
 */
export const ClickableCount: React.FC<ClickableCountProps> = ({
  count,
  onClick,
  className,
  size = 'md',
  showZero = true,
}) => {
  const isClickable = count > 0 && onClick !== undefined;

  if (!showZero && count === 0) {
    return null;
  }

  return (
    <span
      className={cn(
        sizeClasses[size],
        isClickable &&
          'hover:text-chariot-brand-primary cursor-pointer transition-colors',
        className
      )}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      {count.toLocaleString()}
    </span>
  );
};
